<script lang="ts" setup>
  import { useTranslator } from '@othercommerce/foundation';
  import type { PropType } from 'vue';
  import { useBreakpoints } from '@storefront/storefront';
  import type { Breadcrumb, Section } from '@storefront/types';

  const breakpoints = useBreakpoints();

  defineProps({
    module: { type: Object, required: true },
    breadcrumbs: { type: Array as PropType<Breadcrumb[]>, required: false, default: [] },
    sections: { type: Array as PropType<Section[]>, required: false, default: [] },
    departments: { type: Array, required: true },
    main: { type: Object, required: true },
  });

  const translator = useTranslator();

  const days = {
    mon: translator.trans('SHOP.COMMON.LABEL.MONDAY'),
    tue: translator.trans('SHOP.COMMON.LABEL.TUESDAY'),
    wed: translator.trans('SHOP.COMMON.LABEL.WEDNESDAY'),
    thu: translator.trans('SHOP.COMMON.LABEL.THURSDAY'),
    fri: translator.trans('SHOP.COMMON.LABEL.FRIDAY'),
    sat: translator.trans('SHOP.COMMON.LABEL.SATURDAY'),
    sun: translator.trans('SHOP.COMMON.LABEL.SUNDAY'),
  };
</script>
<template>
  <main id="main" class="container contact">
    <SfBreadcrumbs :breadcrumbs="breadcrumbs" />
    <SectionRenderer :sections="sections" placement="before" />
    <section class="contact__wrapper">
      <div class="contact__form-wrapper">
        <header>
          <SfHeading larger type="h1">
            {{ module.title }}
          </SfHeading>
          <div v-if="module.descriptionShort" class="contact__description" v-html="module.descriptionShort" />
        </header>
        <SfContactForm :departments="departments" />
      </div>
      <div class="contact__data-wrapper">
        <section class="contact__data-wrapper-content">
          <SfHeading type="h2">
            {{ $t('SHOP.CONTACT.HEADER.CONTACT_US') }}
          </SfHeading>
          <div class="contact__data-wrapper-contact-flex ">
            <a v-if="main.phone" :href="`tel:${main.phone}`" class="contact__data-wrapper-link">
              <SfIconPhone aria-hidden="true" />
              <span v-html="main.phone" />
            </a>
            <a v-if="main.email" :href="`mailto:${main.email}`" class="contact__data-wrapper-link">
              <SfIconEnvelope aria-hidden="true" />
              <span v-html="main.email" />
            </a>
          </div>
        </section>
        <section class="contact__data-wrapper-content">
          <SfHeading type="h2">
            {{ $t('SHOP.CONTACT.HEADER.ADDRESS') }}
          </SfHeading>
          <div class="contact__department-data">
            <a :href="main.maps || '#'" :title="$t('SHOP.CONTACT.ARIA.SEE_ON_MAP')" class="contact__data-wrapper-address" rel="noreferrer nofollow noopener" target="_blank">
              <span class="contact__data-address-icon">
                <SfIconGeoAlt aria-hidden="true" />
              </span>
              <p class="contact__data-address">
                <span v-if="main.name" v-text="main.name" />
                <span v-if="main.firstLine" v-text="main.firstLine" />
                <span v-if="main.secondLine" v-text="main.secondLine" />
                <span v-if="main.thirdLine" v-text="main.thirdLine" />
              </p>
            </a>
            <div class="contact__hours-wrapper">
              <SfHeading type="h3">
                {{ $t('SHOP.CONTACT.HEADER.OPENING_HOURS') }}
              </SfHeading>
              <div class="contact__hours">
                <template v-for="day in ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const">
                  <div v-if="main.hours[day]" class="contact__day">
                    <span><b>{{ days[day] }}</b></span>
                    <span v-if="main.hours[day].closed" class="contact__day-value">{{ $t('SHOP.CONTACT.LABEL.CLOSED') }}</span>
                    <span v-else class="contact__day-value">{{ main.hours[day].from }} - {{ main.hours[day].to }}</span>
                  </div>
                </template>
              </div>
            </div>
          </div>
        </section>
      </div>
    </section>
    <section v-if="module.photos.length > 0" :class="{'contact__map--link': module.translation?.mapsLink}" class="contact__map">
      <MediaPicture v-if="breakpoints.lg" :model="module" class="brands-carousel__picture" loading="lazy" sizes="2800x800 2x, 1400x400" type="background" />
      <MediaPicture v-else :model="module" class="brands-carousel__picture" loading="lazy" sizes="2800x800 2x, 1120x300" type="banner" />
      <SfButton v-if="module.translation?.mapsLink" :href="module.translation?.mapsLink" class="contact__map-button" primary target="_blank">
        <template #default>
          {{ $t('SHOP.CONTACT.ACTION.GO_TO_MAP') }}
        </template>
        <template #after>
          <SfIconBoxArrowUpRight />
        </template>
      </SfButton>
    </section>
    <SectionRenderer :sections="sections" placement="after" />
  </main>

  <RouterView />
</template>
<style lang="scss" scoped>
  .contact {
    display: flex;
    flex-direction: column;
    gap: var(--sf-section-renderer-gap);

    &__wrapper {
      display: grid;
      grid-template-columns:repeat(2, 1fr);
      gap: 2rem;
    }

    &__description {
      font-size: var(--font-size);
      line-height: var(--line-height);
    }

    &__form-wrapper,
    &__data-wrapper {
      background-color: var(--color-white);
      border-radius: var(--section-border-radius);
      padding: var(--section-padding);
      gap: var(--section-gap);
    }

    &__form-wrapper,
    &__data-wrapper,
    &__data-wrapper-content,
    &__data-wrapper-contact-flex,
    &__department-data,
    &__hours-wrapper,
    &__data-address,
    &__hours {
      display: flex;
      flex-direction: column;
    }

    &__form-wrapper header {
      text-align: center;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 1rem;
    }

    &__map {
      position: relative;
      border-radius: var(--border-radius);
      overflow: hidden;

      :deep(img) {
        height: 100%;
        object-fit: cover;
        aspect-ratio: 1400/400;
      }

      &--link::after {
        content: '';
        position: absolute;
        inset: 0;
        background: var(--backdrop-default);
      }
    }

    &__map-button {
      position: absolute;
      bottom: 1.5rem;
      right: 2rem;
      z-index: 2;
    }

    &__data-wrapper-content {
      gap: 1rem;
    }

    &__data-wrapper-link {
      display: flex;
      align-items: center;
      width: 100%;
      gap: 0.5rem;
      padding: 0.25rem 0;
    }

    &__data-wrapper-address {
      display: flex;
      gap: 0.5rem;
      font-size: 0.875rem;
    }

    &__data-address-icon {
      display: flex;
      justify-content: center;
      align-items: center;
      font-size: 1rem;
      height: 1.25rem;
      width: 1.25rem;
    }

    &__department-data {
      gap: 2rem;
    }

    &__hours-wrapper {
      gap: 1rem;
    }

    &__hours {
      font-size: 0.875rem;
    }

    &__day {
      display: grid;
      grid-template-columns: 1fr 1fr;
      padding: 0.25rem 0.5rem;

      &:nth-child(odd) {
        background-color: var(--background-default);
      }

      b {
        font-weight: var(--font-weight-semi);
      }
    }

    &__day-value {
      text-align: right;
    }

    @media (max-width: 992px) {
      &__wrapper {
        grid-template-columns: 1fr;
        gap: 1rem;
      }

      &__form-wrapper {
        gap: 1rem;
        padding: 1.5rem 0.625rem;
      }

      &__form-wrapper,
      &__data-wrapper,
      &__map {
        border-radius: 0;
      }

      &__map-button {
        top: 50%;
        left: 50%;
        bottom: auto;
        right: auto;
        transform: translate(-50%, -50%);
      }

      &__data-wrapper {
        gap: 1.5rem;
        padding: 1rem 0.625rem;
      }
      &__data-wrapper-content {
        gap: 0.75rem
      }
    }
  }
</style>
